import { useCallback, useEffect, useMemo, useRef } from 'react'

const OUTBOUND_SOURCE = 'plode-web'
const INBOUND_SOURCE = 'plode-scripting'

export function useScriptingBridge(
    iframeRef,
    {
        onReady,
        onRegionEdited,
        onCodeChanged,
        onActionClicked,
        onActionResult,
    } = {}
) {
    const targetOriginRef = useRef(null)

    const cbRef = useRef({})
    cbRef.current = {
        onReady,
        onRegionEdited,
        onCodeChanged,
        onActionClicked,
        onActionResult,
    }

    useEffect(() => {
        const handler = (event) => {
            const data = event.data
            if (!data || typeof data !== 'object') return
            if (data.source !== INBOUND_SOURCE) return
            if (!targetOriginRef.current) targetOriginRef.current = event.origin

            const cb = cbRef.current
            switch (data.type) {
                case 'READY':
                    cb.onReady && cb.onReady()
                    break
                case 'REGION_EDITED':
                    cb.onRegionEdited && cb.onRegionEdited(data.payload)
                    break
                case 'CODE_CHANGED':
                    cb.onCodeChanged && cb.onCodeChanged(data.payload)
                    break
                case 'ACTION_CLICKED':
                    cb.onActionClicked && cb.onActionClicked(data.payload)
                    break
                case 'ACTION_RESULT':
                    cb.onActionResult && cb.onActionResult(data.payload)
                    break
                default:
                    break
            }
        }
        window.addEventListener('message', handler)
        return () => window.removeEventListener('message', handler)
    }, [])

    const post = useCallback(
        (type, payload) => {
            const win = iframeRef.current && iframeRef.current.contentWindow
            if (!win) return
            win.postMessage(
                { source: OUTBOUND_SOURCE, type, payload },
                targetOriginRef.current || '*'
            )
        },
        [iframeRef]
    )

    return useMemo(
        () => ({
            init: (payload) => post('INIT', payload),
            setCode: (code) => post('SET_CODE', { code }),
            setEditableRegions: (regions) =>
                post('SET_EDITABLE_REGIONS', { regions }),
            setAnnotations: (annotations) =>
                post('SET_ANNOTATIONS', { annotations }),
            setEnabledButtons: (buttons) =>
                post('SET_ENABLED_BUTTONS', { buttons }),
            highlightButton: (button) =>
                post('HIGHLIGHT_BUTTON', { button: button || null }),
            triggerAction: (action) => post('TRIGGER_ACTION', { action }),
            reset: () => post('RESET'),
        }),
        [post]
    )
}
